import { createContext, useState, useEffect, useContext } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  signUp: (email: string, password: string, userData: any) => Promise<{ error: any }>;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  loading: boolean;
}

// undefined (e nao `{} as AuthContextType`): com o objeto vazio a guarda de
// useAuth nunca disparava, e usar o hook fora do provider devolvia user
// undefined silenciosamente em vez de quebrar. Foi assim que a persistencia do
// ambiente ficou morta sem ninguem perceber.
const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const init = async () => {
      // PRIMEIRO: obter sessão existente
      const { data: { session }, error } = await supabase.auth.getSession();

      if (mounted) {
        if (error) console.error('[AuthContext] Erro ao obter sessão:', error);
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
      }
    };

    init();

    // DEPOIS: configurar listener para mudanças futuras
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      setUser(session?.user ?? null);

      // Atualizar foto do Google se o perfil já existe mas não tem foto
      if (event === 'SIGNED_IN' && session?.user) {
        setTimeout(async () => {
          try {
            const { data: existingProfile } = await supabase
              .from('users')
              .select('id, profile_photo')
              .eq('auth_id', session.user.id)
              .maybeSingle();

            // Apenas atualizar foto do Google se perfil existe mas não tem foto
            if (existingProfile && !existingProfile.profile_photo) {
              const googleAvatar = session.user.user_metadata?.picture || session.user.user_metadata?.avatar_url;
              if (googleAvatar) {
                await supabase
                  .from('users')
                  .update({ profile_photo: googleAvatar })
                  .eq('auth_id', session.user.id);

              }
            }
          } catch (error) {
            console.error('[AuthContext] Erro ao atualizar foto:', error);
          }
        }, 500);

        // Grava o tipo de conta (prestador/contratante) escolhido na tela de
        // cadastro (Auth.tsx). Funciona pros dois fluxos de signup (email e
        // Google) porque os dois disparam SIGNED_IN aqui — o valor só existe
        // no sessionStorage quando a pessoa acabou de escolher na etapa
        // obrigatória do cadastro, nunca num login normal, então isso nunca
        // afeta contas existentes.
        const pendingAccountType = sessionStorage.getItem('bico_pending_account_type');
        if (pendingAccountType === 'worker' || pendingAccountType === 'contractor') {
          sessionStorage.removeItem('bico_pending_account_type');
          setTimeout(async () => {
            try {
              await supabase
                .from('users')
                .update({ type: pendingAccountType })
                .eq('auth_id', session.user.id);
            } catch (error) {
              console.error('[AuthContext] Erro ao gravar tipo de conta:', error);
            }
          }, 500);
        }
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signUp = async (email: string, password: string, userData: any) => {
    try {
      // Enviar dados do usuário via options.data
      // Dados já vêm sanitizados do Auth.tsx
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/`,
          data: {
            name: userData.name,
            cpf: userData.cpf,
            phone: userData.phone,
            city_id: userData.city_id
          }
        }
      });

      if (error) throw error;
      return { error: null };
    } catch (error: any) {
      console.error('[AuthContext] SignUp error:', error);
      return { error };
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) throw error;
      return { error: null };
    } catch (error: any) {
      return { error };
    }
  };

  const clearAuthStorage = () => {
    // Preserva preferências que não são específicas da sessão do usuário
    // (ex: decisão do prompt de notificações, que não deve resetar a cada logout)
    const preservedKeys = ['notificationPromptSeen', 'notificationPromptLastShown'];
    const preserved = preservedKeys.map((key) => [key, localStorage.getItem(key)] as const);
    localStorage.clear();
    sessionStorage.clear();
    preserved.forEach(([key, value]) => {
      if (value !== null) localStorage.setItem(key, value);
    });
  };

  const signOut = async () => {
    try {
      // Limpar estado local PRIMEIRO
      setUser(null);
      setSession(null);
      // Limpar no Supabase
      await supabase.auth.signOut();
      clearAuthStorage();
      // Forçar redirecionamento e limpar cache
      window.location.href = '/';
    } catch (error: any) {
      console.error('[AuthContext] Error signing out:', error);
      clearAuthStorage();
      window.location.href = '/';
    }
  };

  return (
    <AuthContext.Provider value={{ user, session, signUp, signIn, signOut, loading }}>
      {children}
    </AuthContext.Provider>
  );
};
